

import { Component, Emit, Prop, Vue, Watch } from "vue-property-decorator";
import vSelect3 from "vselect3";
import NotificationRuleCondition from "@/types/sv-data/notification-rule/NotificationRuleCondition";
import AreaTreeSelect from "../form/AreaTreeSelect.vue";
import { AreaNode } from "@/types/sv-data/groups/AreaNode";
import { cloneDeep } from "lodash";
import EventOutcomeSingleSelect from "@/components/form/EventOutcomeSingleSelect.vue";
import NotificationRuleConditionFields from "@/types/sv-data/notification-rule/NotificationRuleConditionFields";
import NotificationRuleConditionOperator from "@/types/sv-data/notification-rule/NotificationRuleConditionOperator";

@Component({
    components: {
        "v-select-3": vSelect3,
        "area-tree-select": AreaTreeSelect,
        "event-outcome-single-select": EventOutcomeSingleSelect   
    },
})
export default class NotificationRuleConditionDetails extends Vue {

    @Prop({ type: Boolean, default: false })
    private readOnly: boolean;

    @Prop({type: Object, required: true})
    private condition: NotificationRuleCondition;

    @Prop({type: Boolean, required: true, default: false})
    private allowDelete: boolean;

    @Prop({ type: Boolean, default: false})
    private isEdit: boolean;

    private selectedArea: number | null = null;
    private originalCondition: NotificationRuleCondition | null = null;
    private selectedOutcome: number | null = null;
    private priorityMax: number = 999999;

    private mounted(): void {
        this.selectedOutcome = null;
        if (this.condition.field === 0) {
            this.selectedArea = isNaN(+this.condition.value) ? null : +this.condition.value;
        }

        if (this.condition.field === 1) {
            this.selectedOutcome = this.isNumber(this.condition.value) ? +this.condition.value : null;
        }

        if (!this.originalCondition) {
            this.originalCondition = cloneDeep(this.condition);
        }

        this.checkValidation();
    }

    private isNumber(str: string): boolean {
        return /^[0-9]+$/.test(str);
    }

    private get fields() { 
        return [{
            title: "Area",
            id: NotificationRuleConditionFields.Area
        },{
            title: "Event Outcome",
            id: NotificationRuleConditionFields.EventOutcome
        },{
            title: "Priority",
            id: NotificationRuleConditionFields.Priority
        }];
    }

    private get operators(): { title: string, id: number}[] {
        switch(this.condition.field) {
            case 0:
            case 1:
                return [{
                    title: "Equals",
                    id: NotificationRuleConditionOperator.Equals
                },{
                    title: "Inherits From",
                    id: NotificationRuleConditionOperator.InheritsFrom
                }];
            case 2:
                return [{
                    title: "Equals",
                    id: NotificationRuleConditionOperator.Equals
                },{
                    title: "Less Than",
                    id: NotificationRuleConditionOperator.LessThan
                },{
                    title: "Greater Than",
                    id: NotificationRuleConditionOperator.GreaterThan
                }];
            default:
                return [{
                    title: "Equals",
                    id: NotificationRuleConditionOperator.Equals
                },{
                    title: "Contains",
                    id: NotificationRuleConditionOperator.Contains
                },{
                    title: "Less Than",
                    id: NotificationRuleConditionOperator.LessThan
                },{
                    title: "Greater Than",
                    id: NotificationRuleConditionOperator.GreaterThan
                },{
                    title: "Inherits From",
                    id: NotificationRuleConditionOperator.InheritsFrom
                }];
        }
    }

    private get actualOutcome(): number | null {
        return this.isNumber(this.condition.value) ? +this.condition.value : null;
    }

    @Emit("updateValid")
    private checkValidation(): boolean {
        return this.isValidCondition();
    }

    @Emit("deleteCondition")
    private onDelete(): void {
        return;
    }

    @Watch("condition")
    private onConditionChange(): void {
        if (!this.condition?.value) {
            this.selectedArea = null;
        }
        this.checkValidation();
    }

    @Watch("condition.field")
    private onFieldChange(): void {
        this.condition.value = "";
        this.condition.operator = null;
        this.selectedArea = null;
        this.selectedOutcome = null;
        this.updateCondition();
    }
    
    public async areaSelected(selectedArea: AreaNode): Promise<void> {
        if (selectedArea === null) {
            this.selectedArea = null;
            this.condition.value = "";
        } else {
            this.selectedArea = selectedArea.id;
            this.condition.value = selectedArea.id.toString();
        }
        this.updateCondition();
    }
    
    private updateCondition(): void {
        this.$emit("updateCondition", this.condition);
        this.checkValidation();
    }

    private isValidCondition(): boolean {
        return !!this.condition.title && 
            this.condition.field >= 0 && 
            this.condition.operator !== null && 
            this.condition.operator >= 0 && 
            !!this.condition.value;
    }

    private onPriorityUpdated(): void {
        this.clampPriority();
        this.updateCondition();
    }

    private clampPriority(): void {
        if (isNaN(+this.condition.value) || +this.condition.value < 0) {
            this.condition.value = "0";
        }

        if (+this.condition.value > this.priorityMax) {
            this.condition.value = this.priorityMax.toString();
        }

        // If value is null, set value to 0, else set value to be a string
        this.condition.value = (this.condition.value ?? 0).toString();
    }
    
    private onOutcomeChange(selectedOutcome: number | null): void {
        this.selectedOutcome = selectedOutcome;
        this.condition.value = `${selectedOutcome ?? ""}`;
        this.updateCondition();
    }
}
